import type {
	FinancialPropertyListItem,
	ServicePeriodListItem,
	ServicePeriodStatusValue,
} from "@/features/financialShared";
import { ServicePeriodStatus } from "@/features/financialShared";
import type { MonthlyUnitListItem } from "./monthlyStatements.types";

export interface MonthlyStatementPermissions {
	canEdit: boolean;
	canPublish: boolean;
}

export type MonthlyStatementListSortBy =
	| "NAME"
	| "TOTAL_UNITS"
	| "LAST_UPDATED";

export interface GetMonthlyStatementPropertiesArgs {
	status?: string;
	sortBy?: MonthlyStatementListSortBy;
	sortOrder?: "ASC" | "DESC";
	page?: number;
	pageSize?: number;
	search?: string;
}

export interface MonthlyStatementPropertiesResponse {
	pageTitle: string;
	lastSyncLabel: string;
	items: FinancialPropertyListItem[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	hasPrevious: boolean;
	hasNext: boolean;
	permissions: MonthlyStatementPermissions;
}

export interface GetMonthlyServicePeriodsArgs {
	propertyId: string;
	page?: number;
	pageSize?: number;
	search?: string;
	uploaded?: boolean;
}

export interface MonthlyServicePeriodsResponse {
	propertyId: string;
	propertyName: string;
	location: string;
	uploadedPeriodsCount: number;
	lastSyncLabel: string;
	items: ServicePeriodListItem[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	canCreate: boolean;
	emptyMessage: string;
}

export interface MonthlyPeriodFileBody {
	filePath: string;
	fileName: string;
	mimeType: string;
	fileSize: number;
}

export interface CreateMonthlyServicePeriodBody {
	periodStart: string;
	periodEnd: string;
	balanceAsOfDate: string;
	file?: MonthlyPeriodFileBody;
}

export interface UploadMonthlyServicePeriodArgs {
	propertyId: string;
	periodId: string;
	body: { file: MonthlyPeriodFileBody };
}

export type { DownloadedFile } from "@/features/financialShared";

export function mapMonthlyStatusFromApi(
	raw: unknown,
): ServicePeriodStatusValue | null {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase();
	if (value === "DRAFT") return ServicePeriodStatus.DRAFT;
	if (value === "PUBLISHED") return ServicePeriodStatus.PUBLISHED;
	if (value === "COMPLETED") return ServicePeriodStatus.COMPLETED;
	return null;
}

export type MonthlyUnitsExportScope = "ALL" | "PENDING";

export interface GetMonthlyServicePeriodUnitsArgs {
	propertyId: string;
	periodId: string;
	page?: number;
	pageSize?: number;
	search?: string;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
}

export interface MonthlyPeriodUnitsHeader {
	propertyId: string;
	propertyName: string;
	location: string;
	periodId: string;
	periodLabel: string;
	periodStart: string;
	periodEnd: string;
	balanceAsOfDate: string;
	uploadedDate: string;
	uploadedBy: string;
	canUpload: boolean;
	canEdit: boolean;
}

export interface MonthlyPeriodUnitsStats {
	totalUnits: number;
	settled: number;
	outstanding: number;
	pastDue: number;
	credit: number;
}

export interface MonthlyServicePeriodUnitsResponse {
	header: MonthlyPeriodUnitsHeader;
	stats: MonthlyPeriodUnitsStats;
	items: MonthlyUnitListItem[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	emptyMessage: string;
}

export interface MonthlyUnitStatementPathArgs {
	propertyId: string;
	periodId: string;
	statementId: string;
}

export interface ExportMonthlyServicePeriodUnitsArgs {
	propertyId: string;
	periodId: string;
	scope: MonthlyUnitsExportScope;
}

export interface MonthlyResumeRowBody {
	label: string;
	amount: number;
}

export interface MonthlyLedgerRowBody {
	id: string;
	date: string;
	invoiceNumber: string;
	code: number;
	concept: string;
	itemType: "DEBIT" | "CREDIT";
	debit: number;
	credit: number;
	balance: number;
	notes: string;
}

export interface UpdateMonthlyUnitStatementBody {
	balanceDueDate: string;
	totalDue: number;
	totalPaid: number;
	resumeRows: MonthlyResumeRowBody[];
	ledgerRows: MonthlyLedgerRowBody[];
}

export interface UpdateMonthlyUnitStatementArgs
	extends MonthlyUnitStatementPathArgs {
	body: UpdateMonthlyUnitStatementBody;
}
